import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Car, MapPin, Calendar, Clock, Users, Search, 
  ArrowRight, Loader2, ChevronRight, ChevronDown,
  Plane, Building2, Luggage, RefreshCw, X, Check,
  Phone, Mail, User, AlertCircle, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Journey type configurations
const JOURNEY_TYPES = [
  { 
    id: 'airport_transfer', 
    label: 'Airport Transfer', 
    icon: Plane,
    description: 'To or from airport',
    color: '#00E5FF'
  },
  { 
    id: 'outstation', 
    label: 'Outstation', 
    icon: Building2,
    description: 'Inter-city travel',
    color: '#FF007F'
  },
  { 
    id: 'local', 
    label: 'Local', 
    icon: Car,
    description: 'Within city',
    color: '#9D4EDD'
  }
];

const TRIP_TYPES = [
  { id: 'oneway', label: 'One Way' },
  { id: 'return', label: 'Round Trip' }
];

// Location Search Component
const LocationSearchInput = ({ 
  label, 
  value, 
  onChange, 
  onSelect, 
  placeholder, 
  icon: Icon,
  iconColor = '#00E5FF',
  testId
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);

  const searchPlaces = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/cabs/search-places`, {
        query: query
      });
      
      if (response.data.success) {
        setSuggestions(response.data.places || []);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Place search error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(true);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchPlaces(newValue);
    }, 300);
  };

  const handleSelect = async (place) => {
    onChange(place.displayLabel || place.name);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Get lat/long for the selected place
    try {
      const response = await axios.post(`${API_URL}/api/cabs/get-lat-long`, {
        place_id: place.id
      });
      
      if (response.data.success) {
        onSelect({
          placeId: place.id,
          displayAddress: place.displayLabel || place.name,
          lat: response.data.lat,
          long: response.data.long,
          type: 'location',
          address: response.data.address || {}
        });
      }
    } catch (err) {
      console.error('Get lat/long error:', err);
      toast.error('Failed to get location details');
    }
  };

  return (
    <div className="relative">
      <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: iconColor }} />
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
          data-testid={testId}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50 animate-spin" />
        )}
      </div>
      
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-[#12101A] border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl max-h-60 overflow-y-auto"
          >
            {suggestions.map((place, index) => (
              <button
                key={place.id || index}
                onClick={() => handleSelect(place)}
                className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#00E5FF] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm">{place.displayLabel || place.name}</p>
                  </div>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Quote Card Component
const QuoteCard = ({ quote, onSelect, selected }) => {
  const vehicleType = quote.vehicleType || quote.vehicle?.type || 'Sedan';
  const vehicleName = quote.vehicleName || quote.vehicle?.name || 'Standard Car';
  const capacity = quote.capacity || quote.vehicle?.capacity || 4;
  const luggage = quote.luggage || quote.vehicle?.luggage || 2;
  const fare = quote.fare || quote.pricing?.totalFare || 0;
  const currency = quote.currency || 'INR';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      onClick={() => onSelect(quote)}
      className={`bg-white/[0.03] backdrop-blur-xl border rounded-2xl p-6 cursor-pointer transition-all ${
        selected 
          ? 'border-[#00E5FF] shadow-[0_0_30px_rgba(0,229,255,0.2)]' 
          : 'border-white/10 hover:border-white/20'
      }`}
      data-testid={`quote-card-${quote.quoteId || quote.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
            selected ? 'bg-[#00E5FF]/20' : 'bg-white/10'
          }`}>
            <Car className={`w-8 h-8 ${selected ? 'text-[#00E5FF]' : 'text-white/70'}`} />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">{vehicleName}</h3>
            <p className="text-white/50 text-sm">{vehicleType}</p>
            <div className="flex items-center gap-4 mt-2 text-white/40 text-xs">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" /> {capacity} seats
              </span>
              <span className="flex items-center gap-1">
                <Luggage className="w-3 h-3" /> {luggage} bags
              </span>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-2xl font-bold text-white">
            {currency === 'INR' ? '₹' : currency} {fare.toLocaleString()}
          </p>
          <p className="text-white/40 text-xs">Total fare</p>
          {selected && (
            <div className="mt-2 flex items-center justify-end gap-1 text-[#00E5FF] text-sm">
              <Check className="w-4 h-4" /> Selected
            </div>
          )}
        </div>
      </div>
      
      {quote.features && quote.features.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="flex flex-wrap gap-2">
            {quote.features.map((feature, index) => (
              <span 
                key={index}
                className="px-2 py-1 bg-white/5 rounded-full text-white/60 text-xs"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Main CabsPage Component
export default function CabsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Form States
  const [journeyType, setJourneyType] = useState('airport_transfer');
  const [tripType, setTripType] = useState('oneway');
  const [originText, setOriginText] = useState('');
  const [destinationText, setDestinationText] = useState('');
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('10:00');
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('10:00');
  const [passengers, setPassengers] = useState(2);
  
  // Search States
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [searched, setSearched] = useState(false);
  
  // Booking States
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [passengerDetails, setPassengerDetails] = useState([{
    name: user?.name || '',
    phone: '',
    email: user?.email || ''
  }]);
  const [contactDetails, setContactDetails] = useState({
    name: user?.name || '',
    phone: '',
    email: user?.email || '',
    countryCode: '+91'
  });
  
  // Service Status
  const [serviceStatus, setServiceStatus] = useState(null);
  
  // Check service status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cabs/status`);
        setServiceStatus(response.data);
      } catch (err) {
        console.error('Service status error:', err);
      }
    };
    checkStatus();
  }, []);
  
  const today = new Date().toISOString().split('T')[0];
  
  const searchQuotes = async () => {
    if (!origin || !destination) {
      toast.error('Please select both pickup and drop locations');
      return;
    }
    
    if (!pickupDate) {
      toast.error('Please select a pickup date');
      return;
    }
    
    if (tripType === 'return' && !returnDate) {
      toast.error('Please select a return date for round trip');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    setQuotes([]);
    setSelectedQuote(null);
    
    try {
      const pickupDateTime = `${pickupDate} ${pickupTime}`;
      const returnDateTime = tripType === 'return' ? `${returnDate} ${returnTime}` : null;
      
      const response = await axios.post(`${API_URL}/api/cabs/quotes`, {
        origin: origin,
        destination: destination,
        pickup_datetime: pickupDateTime,
        journey_type: journeyType,
        trip_type: tripType,
        passengers: passengers,
        return_datetime: returnDateTime
      });
      
      if (response.data.success) {
        const quotesData = response.data.quotes || [];
        setQuotes(quotesData);
        
        if (quotesData.length > 0) {
          toast.success(`Found ${quotesData.length} cab options`);
        } else {
          toast.info('No cabs available for this route/time. TripJack UAT inventory is limited - try a different date or route.');
        }
      } else {
        toast.error(response.data.error || 'Failed to get quotes');
      }
    } catch (err) {
      console.error('Quote search error:', err);
      toast.error('Failed to search for cabs. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBooking = async () => {
    if (!selectedQuote) {
      toast.error('Please select a cab first');
      return;
    }
    
    // Validate passenger details
    const validPassengers = passengerDetails.filter(p => p.name && p.phone && p.email);
    if (validPassengers.length === 0) {
      toast.error('Please fill in passenger details');
      return;
    }
    
    // Validate contact details
    if (!contactDetails.name || !contactDetails.phone || !contactDetails.email) {
      toast.error('Please fill in contact details');
      return;
    }
    
    setBookingLoading(true);
    
    try {
      const pickupDateTime = `${pickupDate} ${pickupTime}`;
      
      const response = await axios.post(`${API_URL}/api/cabs/booking`, {
        quote_id: selectedQuote.quoteId || selectedQuote.id,
        origin: origin,
        destination: destination,
        pickup_datetime: pickupDateTime,
        journey_type: journeyType,
        trip_type: tripType,
        passengers: validPassengers,
        contact: contactDetails
      });
      
      if (response.data.success) {
        toast.success('Booking created successfully!');
        // Navigate to payment/confirmation page
        navigate('/complete-booking', {
          state: {
            booking_type: 'cab',
            booking_id: response.data.booking_id,
            booking_data: response.data.booking_data,
            amount: selectedQuote.fare || selectedQuote.pricing?.totalFare,
            quote: selectedQuote,
            origin: origin,
            destination: destination
          }
        });
      } else {
        toast.error(response.data.error || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking error:', err);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };
  
  const addPassenger = () => {
    if (passengerDetails.length < passengers) {
      setPassengerDetails([...passengerDetails, { name: '', phone: '', email: '' }]);
    }
  };
  
  const updatePassenger = (index, field, value) => {
    const updated = [...passengerDetails];
    updated[index][field] = value;
    setPassengerDetails(updated);
  };
  
  return (
    <div className="min-h-screen bg-[#09080E]" data-testid="cabs-page">
      <FloatingBackButton />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1449965408869-ebd3fee56db8?w=1920&q=80)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09080E]/80 via-[#09080E]/40 to-[#09080E]" />
        
        <div className="relative z-10 h-full flex flex-col justify-end pb-8 px-4 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <p className="text-[#00E5FF] text-sm uppercase tracking-[0.3em] mb-3">
              AIRA Cabs
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight">
              Book Your Ride
            </h1>
            <p className="text-white/60 text-lg mt-3 max-w-xl">
              Airport transfers, outstation trips, and local rides - all in one place
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Search Form */}
      <section className="px-4 md:px-16 -mt-8 relative z-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl max-w-5xl mx-auto"
        >
          {/* Journey Type Selection */}
          <div className="mb-6">
            <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">Journey Type</label>
            <div className="flex flex-wrap gap-3">
              {JOURNEY_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setJourneyType(type.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
                    journeyType === type.id
                      ? 'text-white shadow-lg'
                      : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10 border border-white/10'
                  }`}
                  style={journeyType === type.id ? {
                    background: `linear-gradient(135deg, ${type.color}40, ${type.color}20)`,
                    borderColor: type.color,
                    boxShadow: `0 0 20px ${type.color}30`
                  } : {}}
                  data-testid={`journey-type-${type.id}`}
                >
                  <type.icon className="w-5 h-5" />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Trip Type Toggle */}
          <div className="mb-6">
            <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">Trip Type</label>
            <div className="flex gap-3">
              {TRIP_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setTripType(type.id)}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    tripType === type.id
                      ? 'bg-gradient-to-r from-[#00E5FF] to-[#FF007F] text-white'
                      : 'bg-white/5 text-white/60 hover:text-white border border-white/10'
                  }`}
                  data-testid={`trip-type-${type.id}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Location Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <LocationSearchInput
              label="Pickup Location"
              value={originText}
              onChange={setOriginText}
              onSelect={setOrigin}
              placeholder="Enter pickup address"
              icon={MapPin}
              iconColor="#00E5FF"
              testId="cabs-origin-input"
            />
            <LocationSearchInput
              label="Drop Location"
              value={destinationText}
              onChange={setDestinationText}
              onSelect={setDestination}
              placeholder="Enter drop address"
              icon={MapPin}
              iconColor="#FF007F"
              testId="cabs-destination-input"
            />
          </div>
          
          {/* Date, Time, Passengers */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Pickup Date</label>
              <input
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                min={today}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white"
                data-testid="cabs-pickup-date"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Pickup Time</label>
              <input
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white"
                data-testid="cabs-pickup-time"
              />
            </div>
            {tripType === 'return' && (
              <>
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Return Date</label>
                  <input
                    type="date"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)}
                    min={pickupDate || today}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white"
                    data-testid="cabs-return-date"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Return Time</label>
                  <input
                    type="time"
                    value={returnTime}
                    onChange={(e) => setReturnTime(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white"
                    data-testid="cabs-return-time"
                  />
                </div>
              </>
            )}
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Passengers</label>
              <select
                value={passengers}
                onChange={(e) => setPassengers(parseInt(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white appearance-none"
                data-testid="cabs-passengers"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                  <option key={n} value={n} className="bg-[#12101A]">
                    {n} Passenger{n > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Search Button */}
          <button
            onClick={searchQuotes}
            disabled={loading || !origin || !destination}
            className="w-full bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-black font-semibold py-4 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="search-cabs-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            Search Cabs
          </button>
          
          {/* UAT Notice */}
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
            <p className="text-yellow-500/80 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              UAT Environment: Limited cab inventory available. For best results, try airport routes in major cities.
            </p>
          </div>
        </motion.div>
      </section>
      
      {/* Search Results */}
      <section className="px-4 md:px-16 py-12">
        {searched && (
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 text-[#00E5FF] animate-spin mb-4" />
                <p className="text-white/60">Searching for available cabs...</p>
              </div>
            ) : quotes.length > 0 ? (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl text-white font-bold mb-2">
                    Available Cabs
                  </h2>
                  <p className="text-white/50">
                    {quotes.length} option{quotes.length > 1 ? 's' : ''} found for your trip
                  </p>
                </div>
                
                <div className="space-y-4">
                  {quotes.map((quote, index) => (
                    <QuoteCard
                      key={quote.quoteId || quote.id || index}
                      quote={quote}
                      onSelect={setSelectedQuote}
                      selected={selectedQuote?.quoteId === quote.quoteId || selectedQuote?.id === quote.id}
                    />
                  ))}
                </div>
                
                {/* Proceed to Booking */}
                {selectedQuote && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8"
                  >
                    <button
                      onClick={() => setShowBookingForm(true)}
                      className="w-full bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold py-4 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-2"
                      data-testid="proceed-booking-btn"
                    >
                      Proceed to Book <ArrowRight className="w-5 h-5" />
                    </button>
                  </motion.div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <Car className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl text-white/80 mb-2">No Cabs Available</h3>
                <p className="text-white/50 max-w-md mx-auto">
                  No cabs found for this route and time. TripJack UAT environment has limited inventory. 
                  Try a different date, time, or route (airport transfers often have better availability).
                </p>
              </div>
            )}
          </div>
        )}
      </section>
      
      {/* Booking Form Modal */}
      <AnimatePresence>
        {showBookingForm && selectedQuote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowBookingForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#12101A] border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl text-white font-bold">Complete Booking</h2>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/50" />
                </button>
              </div>
              
              {/* Trip Summary */}
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <h3 className="text-white/70 text-sm mb-3">Trip Summary</h3>
                <div className="flex items-center gap-4">
                  <Car className="w-10 h-10 text-[#00E5FF]" />
                  <div className="flex-1">
                    <p className="text-white font-semibold">
                      {selectedQuote.vehicleName || 'Standard Car'}
                    </p>
                    <p className="text-white/50 text-sm">
                      {originText} → {destinationText}
                    </p>
                    <p className="text-white/50 text-sm">
                      {pickupDate} at {pickupTime}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#00E5FF]">
                      ₹{(selectedQuote.fare || selectedQuote.pricing?.totalFare || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Passenger Details */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-4">Passenger Details</h3>
                {passengerDetails.map((passenger, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">Name</label>
                      <input
                        type="text"
                        value={passenger.name}
                        onChange={(e) => updatePassenger(index, 'name', e.target.value)}
                        placeholder="Full Name"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">Phone</label>
                      <input
                        type="tel"
                        value={passenger.phone}
                        onChange={(e) => updatePassenger(index, 'phone', e.target.value)}
                        placeholder="Phone Number"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">Email</label>
                      <input
                        type="email"
                        value={passenger.email}
                        onChange={(e) => updatePassenger(index, 'email', e.target.value)}
                        placeholder="Email"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                  </div>
                ))}
                
                {passengerDetails.length < passengers && (
                  <button
                    onClick={addPassenger}
                    className="text-[#00E5FF] text-sm hover:underline"
                  >
                    + Add Another Passenger
                  </button>
                )}
              </div>
              
              {/* Contact Details */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-4">Contact Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Contact Name</label>
                    <input
                      type="text"
                      value={contactDetails.name}
                      onChange={(e) => setContactDetails({...contactDetails, name: e.target.value})}
                      placeholder="Contact Name"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Phone</label>
                    <input
                      type="tel"
                      value={contactDetails.phone}
                      onChange={(e) => setContactDetails({...contactDetails, phone: e.target.value})}
                      placeholder="Phone Number"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-white/50 text-xs mb-1 block">Email</label>
                    <input
                      type="email"
                      value={contactDetails.email}
                      onChange={(e) => setContactDetails({...contactDetails, email: e.target.value})}
                      placeholder="Email"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
              </div>
              
              {/* Book Button */}
              <button
                onClick={handleBooking}
                disabled={bookingLoading}
                className="w-full bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-black font-semibold py-4 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="confirm-booking-btn"
              >
                {bookingLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Confirm Booking
                  </>
                )}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
